import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SERVICE_CLASS_LABELS, FUEL_TYPE_LABELS, VEHICLE_KIND_LABELS, VEHICLE_KINDS, VEHICLE_STATUS_LABELS, VEHICLE_STATUSES, formatCents, type FleetOverview, type Paginated, type Vehicle, type VehicleDetail } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { VehicleForm, vehicleBody } from './VehicleForm';
import { LIVE_MS } from './shared';
import { VehicleThumb } from './VehicleThumb';
import { AvailabilityBadge, StateBadge, availabilityNote, errorText, formatDay, kmText, nextServiceText } from './shared';
import './fleet.css';

/**
 * Flotte (KON-167): Übersicht mit fälligen Fristen und Kennzahlen, darunter die Fahrzeugliste. Die Fristen (Hauptuntersuchung, Versicherung, Wartung) stehen in der Übersicht,
 * damit nichts abläuft.
 */
export function FleetPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const kind = params.get('kind') ?? '';
  const due = params.get('due') ?? '';
  const availability = params.get('availability') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const overview = useQuery({ queryKey: ['fleet-overview'], queryFn: async () => (await api<{ data: FleetOverview }>('GET', '/fleet/overview')).data, refetchInterval: LIVE_MS, refetchOnWindowFocus: true });
  const list = useQuery({
    queryKey: ['fleet-vehicles', { q, status, kind, due, availability, sort, page }],
    queryFn: () => api<Paginated<Vehicle>>('GET', `/fleet/vehicles${queryString({ q, status, kind, due, availability, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE_MS, refetchOnWindowFocus: true,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'availability', label: 'Verfügbarkeit', type: 'select', options: [{ value: '', label: 'Alle' }, { value: 'free', label: 'Frei' }, { value: 'in_use', label: 'Im Einsatz' }] },
    { key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Alle' }, ...VEHICLE_STATUSES.map((s) => ({ value: s, label: VEHICLE_STATUS_LABELS[s] }))] },
    { key: 'kind', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...VEHICLE_KINDS.map((k) => ({ value: k, label: VEHICLE_KIND_LABELS[k] }))] },
    { key: 'due', label: 'Fristen', type: 'select', options: [{ value: '', label: 'Alle' }, { value: '1', label: 'HU oder Versicherung fällig' }] },
  ];
  const create = useMutation({
    mutationFn: (d: Parameters<typeof vehicleBody>[0]) => api<{ data: VehicleDetail }>('POST', '/fleet/vehicles', vehicleBody(d, { create: true, withCost: can('fleet.values') })),
    onSuccess: (r) => {
      for (const key of ['fleet-vehicles', 'fleet-overview']) client.invalidateQueries({ queryKey: [key] });
      navigate(`/flotte/${r.data.id}`);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Das Anlegen hat nicht geklappt.')),
  });
  const data = overview.data;
  const overdue = data?.deadlines.filter((d) => d.state === 'overdue').length ?? 0;

  return (
    <>
      <div className="page-head">
        <h1>Flotte</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} Fahrzeuge` : ''}</span>
        {can('fleet.write') && <button type="button" className="btn btn--primary" onClick={() => { setError(null); setCreating(true); }}>Neues Fahrzeug</button>}
      </div>
      {data && (
        <div className="fleet-tiles">
          <div className="card fleet-tile"><span className="muted small">Fahrzeuge im Betrieb</span><strong>{data.vehicles.byStatus.active + data.vehicles.byStatus.workshop}</strong>{data.vehicles.byStatus.workshop > 0 && <span className="muted small">{data.vehicles.byStatus.workshop} in der Werkstatt</span>}</div>
          <div className="card fleet-tile"><span className="muted small">Fristen fällig</span><strong className={data.deadlines.length ? (overdue ? 'fleet-bad' : 'fleet-warn') : ''}>{data.deadlines.length}</strong>{overdue > 0 && <span className="fleet-bad small">{overdue} überfällig</span>}</div>
          <div className="card fleet-tile"><span className="muted small">Kilometer dieses Jahr</span><strong>{(data.kmThisYear.business + data.kmThisYear.private + data.kmThisYear.commute).toLocaleString('de-DE')} km</strong><span className="muted small">{data.kmThisYear.business.toLocaleString('de-DE')} geschäftlich</span></div>
          {data.costsThisYearCents !== null && <div className="card fleet-tile"><span className="muted small">Kosten dieses Jahr</span><strong>{formatCents(data.costsThisYearCents)}</strong><span className="muted small">Kosten und Wartung, brutto</span></div>}
          {data.insuranceForecast && (
            <div className="card fleet-tile">
              <span className="muted small">Versicherung je Monat</span>
              <strong>{formatCents(data.insuranceForecast.monthlyCents)}</strong>
              <span className="muted small">{formatCents(data.insuranceForecast.yearlyCents)} im Jahr (Prognose)</span>
              {data.insuranceForecast.uninsured > 0 && <span className="fleet-warn small">{data.insuranceForecast.uninsured} Fahrzeuge ohne Vertrag</span>}
              {data.insuranceForecast.withoutPremium > 0 && <span className="muted small">{data.insuranceForecast.withoutPremium} Verträge ohne Beitrag</span>}
            </div>
          )}
        </div>
      )}
      {data && data.deadlines.length > 0 && (
        <section className="card" aria-labelledby="fleet-due">
          <h2 id="fleet-due">Fristen</h2>
          <ul className="fleet-list">
            {data.deadlines.map((d, i) => (
              <li key={`${d.vehicleId}-${d.type}-${i}`}>
                <StateBadge state={d.state} /> <Link to={`/flotte/${d.vehicleId}`} className="strong-link">{d.plate}</Link> · {d.label}
                {d.dueOn && <> · {d.state === 'overdue' ? 'seit' : 'am'} {formatDay(d.dueOn)}</>}
                {d.remainingKm !== null && <> · {d.remainingKm < 0 ? `${kmText(-d.remainingKm)} überschritten` : `noch ${kmText(d.remainingKm)}`}</>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {creating && <VehicleForm onSubmit={(d) => { setError(null); create.mutate(d); }} onCancel={() => setCreating(false)} pending={create.isPending} error={error} submitLabel="Anlegen" />}
      <FilterBar search={{ value: q, placeholder: 'Kennzeichen, Nummer, Modell oder Fahrer', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ availability, status, kind, due }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Kennzeichen" sortKey="plate" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Fahrzeug" sortKey="name" sort={sort} onSort={(s) => update({ sort: s })} />
              <th>Fahrer</th>
              <SortTh label="Kilometer" sortKey="km" sort={sort} onSort={(s) => update({ sort: s })} first="desc" className="num" />
              <th>Nächster Service</th>
              <SortTh label="HU/AU" sortKey="huDueOn" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Status" sortKey="status" sort={sort} onSort={(s) => update({ sort: s })} />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={8} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={8} className="empty">{q || status || kind || due || availability ? 'Keine Fahrzeuge zu dieser Suche.' : 'Noch kein Fahrzeug erfasst.'}</td></tr>}
            {list.data?.data.map((v) => (
              <ExpandRow
                key={v.id}
                className={v.status === 'sold' || v.status === 'retired' ? 'inv-reversed' : ''}
                summary={<Summary title={<span className="fleet-id"><VehicleThumb vehicle={v} small /><Link to={`/flotte/${v.id}`} className="strong-link">{v.plate}</Link></span>} aside={<AvailabilityBadge vehicle={v} />} line2={[v.name, [v.manufacturer, v.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || undefined} line3={[kmText(v.currentKm), v.employeeName, availabilityNote(v), v.nextService ? `Service ${nextServiceText(v.nextService)}` : null].filter(Boolean).join(' · ')} />}
              >
                <td className="cell-desktop-title"><div className="fleet-id"><VehicleThumb vehicle={v} /><div><Link to={`/flotte/${v.id}`} className="strong-link">{v.plate}</Link><div className="muted small">{v.number}</div></div></div></td>
                <td data-label="Fahrzeug">{v.name ?? ([v.manufacturer, v.model].filter(Boolean).join(' ') || '–')}<div className="muted small">{[VEHICLE_KIND_LABELS[v.kind], FUEL_TYPE_LABELS[v.fuel], v.serviceClass ? SERVICE_CLASS_LABELS[v.serviceClass] : ''].filter(Boolean).join(' · ')}</div></td>
                <td data-label="Fahrer">{v.employeeName ?? '–'}</td>
                <td data-label="Kilometer" className="num">{kmText(v.currentKm)}</td>
                <td data-label="Nächster Service">
                  {v.nextService ? (
                    <>
                      {nextServiceText(v.nextService)}
                      <div className="muted small">{v.nextService.label} <StateBadge state={v.nextService.state === 'ok' ? null : v.nextService.state} /></div>
                    </>
                  ) : (
                    <span className="muted">–</span>
                  )}
                </td>
                <td data-label="HU/AU">{formatDay(v.huDueOn)} <StateBadge state={v.huState === 'ok' ? null : v.huState} /></td>
                <td data-label="Status"><AvailabilityBadge vehicle={v} />{availabilityNote(v) && <div className="muted small">{availabilityNote(v)}</div>}{v.insuranceState && v.insuranceState !== 'ok' && <div className="small"><StateBadge state={v.insuranceState} label={`Versicherung ${v.insuranceState === 'overdue' ? 'abgelaufen' : 'läuft ab'}`} /></div>}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
